import React, { useEffect, useRef, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  Search,
  Filter,
  X,
  ChevronDown,
  RefreshCw,
  ShoppingBag,
  Tag,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useShoppingDealsPage } from "@/hooks/use-shopping-deals-page";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DealProps } from "@/hooks/use-shopping-deals";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const DealCard = ({
  image,
  title,
  originalPrice,
  salePrice,
  discount,
  store,
  link,
  delay,
  id,
  index,
}: DealProps & { index?: number }) => (
  <div
    className="rounded-xl overflow-hidden glass-card hover:shadow-md hover:-translate-y-3 animate-fade-in opacity-0 bg-white shadow-sm transition-all duration-500"
    style={{ animationDelay: `${(delay || 0) + (index || 0) * 0.05}s` }}
  >
    <div className="relative">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
        />
      </div>
      <div className="absolute top-3 right-3 py-1 px-2 bg-gold text-navy text-xs font-bold rounded-md flex items-center">
        <Tag className="h-3 w-3 mr-1" />
        {discount} OFF
      </div>
      <div className="absolute top-3 left-3">
        <span className="bg-white/90 text-navy px-3 py-1 rounded-full text-xs font-medium">
          {store}
        </span>
      </div>
    </div>
    <div className="p-5">
      <h3 className="text-navy font-medium mb-3 text-lg line-clamp-2">
        {title}
      </h3>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-navy font-bold">{salePrice}</span>
        <span className="text-navy/60 text-sm line-through">
          {originalPrice}
        </span>
      </div>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full"
      >
        <Button className="w-full bg-navy hover:bg-navy-light justify-center group transition-all duration-300 hover:scale-105">
          <ShoppingBag className="h-4 w-4 mr-2" />
          <span>Shop Now</span>
          <ExternalLink className="h-3 w-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
        </Button>
      </a>
    </div>
  </div>
);

const Deals = () => {
  const {
    deals,
    isLoading,
    loadingMore,
    error,
    hasMore,
    searchQuery,
    selectedStores,
    selectedPriceRanges,
    discountSortOrder,
    availableStores,
    priceRanges,
    handleSearchChange,
    handleStoreChange,
    handlePriceRangeChange,
    handleDiscountSortChange,
    resetFilters,
    loadMoreDeals,
  } = useShoppingDealsPage();

  // Ref cho last deal element để phát hiện scroll
  const observer = useRef<IntersectionObserver | null>(null);
  const lastDealElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || loadingMore) return;

      // Disconnect existing observer
      if (observer.current) observer.current.disconnect();

      // Create new observer
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreDeals();
          }
        },
        { threshold: 0.5 }
      );

      // Observe new node if exists
      if (node) observer.current.observe(node);
    },
    [isLoading, loadingMore, hasMore, loadMoreDeals]
  );

  // Set page title
  useEffect(() => {
    document.title = `Shopping Deals | GlowHer.Life`;
  }, []);

  // Hiệu ứng reveal nội dung khi scrolling
  useEffect(() => {
    if (!isLoading) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      // Select all elements with animation-ready class
      const animatedElements = document.querySelectorAll(".animation-ready");
      animatedElements.forEach((el) => observer.observe(el));

      return () => {
        animatedElements.forEach((el) => observer.unobserve(el));
      };
    }
  }, [isLoading]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Hero section */}
        <section className="relative py-16 md:py-24 bg-[#1A2B4D] overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

          <div className="container mx-auto px-4 md:px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              <span
                className="inline-block px-3 py-1 bg-white/10 text-white text-sm rounded-full mb-4 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.1s" }}
              >
                <ShoppingBag className="h-4 w-4 inline mr-2" />
                Exclusive Offers
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                Discover Shopping Deals
              </h1>
              <p
                className="text-xl text-white/90 leading-relaxed animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                Explore our handpicked selection of the best shopping deals and
                special offers from premium brands
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter */}
        <section
          className="py-8 bg-gray-50 sticky top-16 z-10 border-b border-gray-200 shadow-sm animate-fade-in opacity-0"
          style={{ animationDelay: "0.4s" }}
        >
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col gap-4">
              {/* Search and filter controls */}
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="relative w-full md:w-96">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search deals..."
                    className="pl-10 bg-white transition-all duration-300 focus:ring-2 focus:ring-gold/20"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>

                <div className="flex gap-2 w-full md:w-auto justify-between md:justify-end">
                  {/* Stores filter dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex-1 md:flex-none hover:bg-gold/5 transition-colors group"
                      >
                        <Filter className="mr-2 h-4 w-4" />
                        Stores
                        <ChevronDown className="ml-2 h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 max-h-[70vh] overflow-auto dropdown-animation">
                      <DropdownMenuLabel>Select Stores</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {availableStores.map((store) => (
                          <DropdownMenuItem
                            key={store}
                            className={`cursor-pointer flex items-center justify-between ${
                              selectedStores.includes(store)
                                ? "bg-gray-100"
                                : ""
                            } hover:bg-gold/5 transition-colors`}
                            onClick={() =>
                              handleStoreChange(
                                store,
                                !selectedStores.includes(store)
                              )
                            }
                          >
                            {store}
                            {selectedStores.includes(store) && (
                              <X className="h-4 w-4 text-gray-500" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Price range filter dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex-1 md:flex-none hover:bg-gold/5 transition-colors group"
                      >
                        <Filter className="mr-2 h-4 w-4" />
                        Price Range
                        <ChevronDown className="ml-2 h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 dropdown-animation">
                      <DropdownMenuLabel>Select Price Range</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {priceRanges.map((range) => (
                          <DropdownMenuItem
                            key={range.id}
                            className={`cursor-pointer flex items-center justify-between ${
                              selectedPriceRanges.includes(range.id)
                                ? "bg-gray-100"
                                : ""
                            } hover:bg-gold/5 transition-colors`}
                            onClick={() =>
                              handlePriceRangeChange(
                                range.id,
                                !selectedPriceRanges.includes(range.id)
                              )
                            }
                          >
                            {range.name}
                            {selectedPriceRanges.includes(range.id) && (
                              <X className="h-4 w-4 text-gray-500" />
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Discount sort dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex-1 md:flex-none hover:bg-gold/5 transition-colors group"
                      >
                        <Filter className="mr-2 h-4 w-4" />
                        Discount
                        <ChevronDown className="ml-2 h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 dropdown-animation">
                      <DropdownMenuLabel>Sort by Discount</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem
                          className={`cursor-pointer ${
                            discountSortOrder === "desc" ? "bg-gray-100" : ""
                          } hover:bg-gold/5 transition-colors`}
                          onClick={() => handleDiscountSortChange("desc")}
                        >
                          Highest to Lowest
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className={`cursor-pointer ${
                            discountSortOrder === "asc" ? "bg-gray-100" : ""
                          } hover:bg-gold/5 transition-colors`}
                          onClick={() => handleDiscountSortChange("asc")}
                        >
                          Lowest to Highest
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Reset filters button */}
                  {(selectedStores.length > 0 ||
                    selectedPriceRanges.length > 0 ||
                    discountSortOrder !== null ||
                    searchQuery.trim() !== "") && (
                    <Button
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50 flex-1 md:flex-none transition-all duration-300"
                      onClick={resetFilters}
                    >
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin-slow" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>

              {/* Active filters display */}
              {!isLoading &&
                !error &&
                (selectedStores.length > 0 ||
                  selectedPriceRanges.length > 0 ||
                  discountSortOrder !== null) && (
                  <div
                    className="flex flex-wrap gap-2 mt-2 animate-fade-in opacity-0"
                    style={{ animationDelay: "0.5s" }}
                  >
                    {selectedStores.map((store) => (
                      <div
                        key={store}
                        className="bg-[#1A2B4D] text-white text-xs rounded-full px-3 py-1.5 flex items-center transition-all duration-300 hover:bg-[#2E3F66]"
                      >
                        {store}
                        <button
                          className="ml-2 hover:opacity-80"
                          onClick={() => handleStoreChange(store, false)}
                          aria-label={`Remove ${store} filter`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {selectedPriceRanges.map((rangeId) => {
                      const range = priceRanges.find((r) => r.id === rangeId);
                      if (!range) return null;
                      return (
                        <div
                          key={rangeId}
                          className="bg-gold text-[#1A2B4D] text-xs rounded-full px-3 py-1.5 flex items-center transition-all duration-300 hover:bg-gold-dark"
                        >
                          {range.name}
                          <button
                            className="ml-2 hover:opacity-80"
                            onClick={() =>
                              handlePriceRangeChange(rangeId, false)
                            }
                            aria-label={`Remove ${range.name} filter`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                    {discountSortOrder && (
                      <div className="bg-purple-600 text-white text-xs rounded-full px-3 py-1.5 flex items-center transition-all duration-300 hover:bg-purple-700">
                        Discount:{" "}
                        {discountSortOrder === "desc"
                          ? "Highest to Lowest"
                          : "Lowest to Highest"}
                        <button
                          className="ml-2 hover:opacity-80"
                          onClick={() => handleDiscountSortChange(null)}
                          aria-label="Remove discount sort"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </section>

        {/* Deals Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            {isLoading && !deals.length ? (
              <div
                className="text-center py-12 animate-fade-in opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">
                  Loading deals...
                </h3>
                <p className="text-[#1A2B4D]/70">
                  Please wait while we load the best shopping deals.
                </p>
              </div>
            ) : error ? (
              <div
                className="text-center py-12 animate-fade-in opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">!</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">
                  An error occurred
                </h3>
                <p className="text-[#1A2B4D]/70">{error}</p>
                <Button
                  className="mt-4 transition-all duration-300 hover:scale-105"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </Button>
              </div>
            ) : deals.length > 0 ? (
              <>
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 animate-fade-in"
                  style={{ animationDelay: "0.4s" }}
                >
                  {deals.map((deal, index) => {
                    // If it's the last deal, add a ref to detect scroll
                    if (deals.length === index + 1) {
                      return (
                        <div ref={lastDealElementRef} key={deal.id}>
                          <DealCard {...deal} index={index} />
                        </div>
                      );
                    } else {
                      return <DealCard key={deal.id} {...deal} index={index} />;
                    }
                  })}
                </div>

                {/* Loading spinner when loading more */}
                {loadingMore && (
                  <div
                    className="text-center py-8 mt-8 animate-fade-in opacity-0"
                    style={{ animationDelay: "0.3s" }}
                  >
                    <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#1A2B4D]/70">
                      Loading more deals...
                    </p>
                  </div>
                )}

                {/* Notice when all deals are loaded */}
                {!hasMore && deals.length > 8 && (
                  <div
                    className="text-center py-8 mt-8 animate-fade-in opacity-0"
                    style={{ animationDelay: "0.3s" }}
                  >
                    <p className="text-[#1A2B4D]/70">
                      All deals have been displayed
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div
                className="text-center py-12 animate-fade-in opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">
                  No deals found
                </h3>
                <p className="text-[#1A2B4D]/70">
                  Try changing your search criteria or check back later for new
                  deals.
                </p>
                {(selectedStores.length > 0 ||
                  selectedPriceRanges.length > 0 ||
                  discountSortOrder !== null ||
                  searchQuery) && (
                  <Button
                    className="mt-4 transition-all duration-300 hover:scale-105"
                    onClick={resetFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Deals;
